import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { ToastContainer, toast } from 'react-toastify';
import { useAuth } from 'react-oidc-context';
import TickerRow from '../../TickerRow/TickerRow'; 
import DataPeriodAlert from '../backtest/partials/DataPeriodAlert';
import NewPortfolioModal from '../portfolios/modal/NewPortfolioModal';
import { usePortfolioActions } from '../backtest/hooks/usePortfolioActions';
import { useMonteCarlo } from "./hooks/useMonteCarlo";
import { getCurrencySymbol } from "./utils/MonteCarloUtils";
import { GrowthChart, HistogramChart } from "./charts/MonteCarloCharts";
import ChallengeCta from "../../partials/ChallengeCta/ChallengeCta";

import styles from "./style/MonteCarlo.module.css";
import commonStyles from "../../../styles/Common.module.css"; 

// ==========================================
// 1. Helper Components (반복되는 UI 분리)
// ==========================================

const ShareButton = ({ onClick, t }) => (
    <button onClick={onClick} className={styles.shareBtn} style={{ margin: '10px 0', backgroundColor: '#5cb85c' }}>
        🔗 {t("shareLink") || t("montecarlo.shareLink")}
    </button>
);

const MiniPlayer = ({ year, setYear, isPlaying, setIsPlaying, maxYear, t }) => {
    const togglePlay = () => year >= maxYear ? (setYear(1), setIsPlaying(true)) : setIsPlaying(!isPlaying);
    return (
        <div className={styles.miniPlayerContainer}>
            <button onClick={togglePlay} className={`${styles.playBtn} ${isPlaying ? styles.playing : ''}`}>
                {isPlaying ? '⏸' : '▶'}
            </button>
            <input 
                type="range" min="0" max={maxYear} value={year} 
                onChange={(e) => { setYear(Number(e.target.value)); setIsPlaying(false); }} 
                className={styles.timelineSlider}
            />
            <span className={styles.timelineLabel}>
                {year === 0 ? t('montecarlo.chart.present') : t('montecarlo.chart.afterYears', { year })}
            </span>
        </div>
    );
};

const KPIBox = ({ label, value, sub, boxLvl, textLvl, extraSubClass = "" }) => {
    // boxLvl: 1(Excellent), 2(Good), 3(Warning), 4(Critical)
    const boxClasses = [styles.kpiBoxExcellent, styles.kpiBoxGood, styles.kpiBoxWarning, styles.kpiBoxCritical];
    const textClasses = [styles.textExcellent, styles.textSuccess, styles.textWarning, styles.textDanger];
    
    return (
        <div className={`${styles.kpiBox} ${boxClasses[boxLvl - 1]}`}>
            <span className={styles.kpiLabel}>{label}</span>
            <span className={`${styles.kpiValue} ${textClasses[textLvl - 1]}`}>{value}</span>
            <span className={`${styles.kpiSub} ${extraSubClass}`}>{sub}</span>
        </div>
    );
};



// ==========================================
// 2. MilestoneTable Component (거대한 로직 분리)
// ==========================================
const MilestoneTable = ({ simulationResults, milestoneData, inputs, calculateMilestoneData, t, showAllYears, setShowAllYears }) => {
    if (!simulationResults || !milestoneData) return null;
    const currencySymbol = getCurrencySymbol(inputs.currency);
    const displayData = showAllYears ? calculateMilestoneData(simulationResults, false) : milestoneData;
    if (!displayData || displayData.length === 0) return null;

    const isWithdrawal = inputs.isWithdrawalMode;
    const target = Number(inputs.targetAmount || 0);
    const hasTarget = !isWithdrawal && target > 0;

    const getSurvivalColorClass = (rate) => rate >= 90 ? styles.textSafe : rate >= 70 ? styles.textCaution : styles.textDanger;
    const getGoalColorClass = (rate) => rate >= 80 ? styles.textSafe : rate >= 50 ? styles.textPrimary : styles.textWarning;

    const renderValue = (val, currentPrincipal, isHit, isDepleted) => {
        const formatted = Math.round(val).toLocaleString() + currencySymbol;
        if (isWithdrawal && val <= 0) return <span className={styles.textDepleted}>{formatted}</span>;
        return <span className={val < currentPrincipal ? styles.textLoss : ""}>{formatted}</span>;
    };

    return (
        <div className={styles.milestoneSection}>
            <div className={styles.milestoneHeader}>
                <h3 className={styles.milestoneTitle}>📊 {t('montecarlo.milestone.title')}</h3>
                <div className={styles.milestoneToggleGroup}>
                    <button className={`${styles.milestoneToggleBtn} ${!showAllYears ? styles.active : ''}`} onClick={() => setShowAllYears(false)}>{t('montecarlo.milestone.fiveYear')}</button>
                    <button className={`${styles.milestoneToggleBtn} ${showAllYears ? styles.active : ''}`} onClick={() => setShowAllYears(true)}>{t('montecarlo.milestone.allYears')}</button>
                </div>
            </div>
            <div className={styles.milestoneTableWrapper}>
                <table className={styles.milestoneTable}>
                    <thead>
                        <tr>
                            <th>{t('montecarlo.milestone.headers.year')}</th>
                            <th>{t('montecarlo.milestone.headers.p10')}</th>
                            <th>{t('montecarlo.milestone.headers.median')}</th>
                            <th>{t('montecarlo.milestone.headers.p90')}</th>
                            {/* 원금 열은 항상 유지 (출금 모드 제외) */}
                            {!isWithdrawal && <th>{t('montecarlo.milestone.headers.principal')}</th>}
                            {/* 마지막 성공 지표 열 추가 */}
                            <th>
                                {isWithdrawal 
                                    ? t('montecarlo.milestone.headers.survivalRate') 
                                    : (hasTarget ? t('montecarlo.milestone.headers.goalRate', 'Goal Probability') : t('montecarlo.milestone.headers.principal'))
                                }
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayData.map(({ year, p10, p50, p90, principal, survivalRate, goalReachRate }) => {
                            const roi = principal > 0 ? ((p50 - principal) / principal) * 100 : 0;
                            const isDepleted = isWithdrawal && p50 <= 0;
                            const isAccum = !isWithdrawal && target > 0;
                            
                            const rowClass = `${year % 5 === 0 ? styles.milestoneHighlight : ''} ${isDepleted ? styles.rowDepleted : ''}`.trim();

                            return (
                                <tr key={year} className={rowClass}>
                                    <td>{t('montecarlo.milestone.yearLabel', { year })} {isDepleted && "⚠️"}</td>
                                    <td className={`${styles.milestoneValueLow} ${isAccum && p10 >= target ? styles.textTargetHit : ""}`}>
                                        {renderValue(p10, principal, isAccum && p10 >= target)}                
                                    </td>
                                    <td className={`${styles.milestoneValueMed} ${styles.textBold} ${isAccum && p50 >= target ? styles.textTargetHit : ""}`}>
                                        {renderValue(p50, principal, isAccum && p50 >= target)}                             
                                        {!isWithdrawal && !(isAccum && p50 >= target) && 
                                            <span className={`${styles.roiTag} ${roi < 0 ? styles.roiTagNegative : ""}`}>{roi >= 0 ? "+" : ""}{roi.toFixed(0)}%</span>
                                        }
                                    </td>
                                    <td className={`${styles.milestoneValueHigh} ${isAccum && p90 >= target ? styles.textTargetHit : ""}`}>
                                        {renderValue(p90, principal, isAccum && p90 >= target)}
                                    </td>

                                    {/* 원금 데이터 행 (적립 모드일 때만 표시) */}
                                    {!isWithdrawal && (
                                        <td className={styles.textMuted}>
                                            {principal.toLocaleString()}{currencySymbol}
                                        </td>
                                    )}

                                    {/* 성공 확률 데이터 행 */}
                                    {isWithdrawal ? (
                                        <td className={`${styles.textBold} ${getSurvivalColorClass(survivalRate)}`}>
                                            {survivalRate.toFixed(1)}%
                                        </td>
                                    ) : (
                                        <td className={`${styles.textBold} ${getGoalColorClass(goalReachRate)}`}>
                                            {goalReachRate.toFixed(1)}%
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// ==========================================
// 3. Main Component
// ==========================================
function MonteCarlo() {
    const { t } = useTranslation();
    const auth = useAuth();
    const navigate = useNavigate();

    const {
        rows, setRows, inputs, updateInput, isLoading, simulationResults, milestoneData, metaInfo,
        runSimulation, calculateTotalWeight, calculateMilestoneData
    } = useMonteCarlo(t);

    // 👇 여기에 아래 두 줄을 추가하세요.
    const stocks = rows
    .filter(row => row.ticker && row.ticker.trim() !== "") // 티커가 있는 것만 필터링
    .map(row => row.ticker);

    const optimizedWeights = rows.reduce((acc, row) => {
        if (row.ticker && row.ticker.trim() !== "") {
            acc[row.ticker] = row.weight;
        }
        return acc;
    }, {});

    const [growthYear, setGrowthYear] = useState(inputs.simYears);
    const [isGrowthPlaying, setIsGrowthPlaying] = useState(false);
    const [distYear, setDistYear] = useState(inputs.simYears);
    const [isDistPlaying, setIsDistPlaying] = useState(false);
    const [mddYear, setMddYear] = useState(inputs.simYears);
    const [isMddPlaying, setIsMddPlaying] = useState(false);
    const [showAllYears, setShowAllYears] = useState(false);
    
    // 툴팁 관련 상태 추가
    const [isInfoTooltipOpen, setIsInfoTooltipOpen] = useState(false);
    const infoTooltipRef = useRef(null);
    const [isInflationTooltipOpen, setIsInflationTooltipOpen] = useState(false);
    const inflationTooltipRef = useRef(null);

    // Tooltip 닫기 이벤트 (두 개의 툴팁 모두 처리)
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (infoTooltipRef.current && !infoTooltipRef.current.contains(e.target)) setIsInfoTooltipOpen(false);
            if (inflationTooltipRef.current && !inflationTooltipRef.current.contains(e.target)) setIsInflationTooltipOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (simulationResults) {
            const max = inputs.simYears;
            setGrowthYear(max); setIsGrowthPlaying(false);
            setDistYear(max); setIsDistPlaying(false);
            setMddYear(max); setIsMddPlaying(false);
        }
    }, [simulationResults, inputs.simYears]);

    const useAnimationLoop = (isPlaying, setYear, setIsPlaying, maxYear) => {
        useEffect(() => {
            if (!isPlaying) return;
            const interval = setInterval(() => setYear(prev => {
                if (prev + 1 >= maxYear) { clearInterval(interval); setTimeout(() => setIsPlaying(false), 0); return maxYear; }
                return prev + 1;
            }), 300);
            return () => clearInterval(interval);
        }, [isPlaying, maxYear, setYear, setIsPlaying]);
    };

    useAnimationLoop(isGrowthPlaying, setGrowthYear, setIsGrowthPlaying, inputs.simYears);
    useAnimationLoop(isDistPlaying, setDistYear, setIsDistPlaying, inputs.simYears);
    useAnimationLoop(isMddPlaying, setMddYear, setIsMddPlaying, inputs.simYears);

    const handleRunSimulation = () => {
      const isEmpty = rows.length === 0 || (rows.length === 1 && !rows[0].ticker.trim());
      if (isEmpty) {
          toast.info(t('montecarlo.autoLoadExample', 'No tickers entered. Running simulation with example data (VOO/BND).'));
          setRows(exampleTickers);
          runSimulation(false, exampleTickers); 
      } else runSimulation(false);
    };

    const currencySymbol = getCurrencySymbol(inputs.currency);
    const weightSum = calculateTotalWeight();
    const exampleTickers = [ { ticker: "VOO.US", weight: "70" }, { ticker: "BND.US", weight: "30" } ];

    const actions = usePortfolioActions(auth, {
        rows, setRows, validRows: rows.filter(row => row.ticker && Number(row.weight) > 0),
        totalWeight: weightSum, message: "", setMessage: () => {}, navigate,
    }, t, navigate);

    const handleRowChange = (idx, field, val) => setRows(rows.map((r, i) => i === idx ? { ...r, [field]: val } : r));
    const handleShareLink = () => { navigator.clipboard.writeText(window.location.href); toast.info(t("linkCopied") || t("montecarlo.linkCopied")); };
    
    useEffect(() => { document.title = 'QuanPort - Monte Carlo Simulation'; }, []);

    // 결과 렌더링 헬퍼 함수
    const renderKPIs = () => {
        const sr = simulationResults;
        if (inputs.isWithdrawalMode) {
            const getSurvLvl = val => val >= 90 ? 1 : val >= 80 ? 2 : val >= 70 ? 3 : 4;
            const getRuinLvl = val => val === Infinity || val >= inputs.simYears ? 1 : val >= inputs.minSurvivalYears * 1.5 ? 2 : val >= inputs.minSurvivalYears ? 3 : 4;
            const getMddLvl = val => val < 20 ? 2 : val < 35 ? 3 : 4;

            const mddP50 = (sr.mddPercentiles?.p50 * 100) || 0;
            return <>
                <KPIBox label={t('montecarlo.kpi.survivalRate')} value={`${sr.survivalRate?.toFixed(1) || 0}%`} sub={t('montecarlo.kpi.survivalRateSub', { years: inputs.minSurvivalYears })} boxLvl={getSurvLvl(sr.survivalRate)} textLvl={getSurvLvl(sr.survivalRate)} />
                <KPIBox label={t('montecarlo.kpi.ruinYear')} value={sr.medianRuinYear >= inputs.simYears ? t('montecarlo.kpi.ruinYearSafe') : t('montecarlo.kpi.ruinYearAfter', { year: sr.medianRuinYear })} sub={`${t('montecarlo.milestone.headers.p10')}: ${sr.p10RuinYear >= inputs.minSurvivalYears ? t('montecarlo.kpi.ruinYearSub.safe') : t('montecarlo.kpi.ruinYearSub.depleted', { year: sr.p10RuinYear })}`} boxLvl={getRuinLvl(sr.medianRuinYear)} textLvl={getRuinLvl(sr.medianRuinYear)} extraSubClass={sr.p10RuinYear < inputs.minSurvivalYears ? styles.textDanger : ''} />
                <KPIBox label={t('montecarlo.kpi.maxDrawdown')} value={`-${mddP50.toFixed(1)}%`} sub={t('montecarlo.kpi.maxDrawdownSub', { value: (sr.mddPercentiles?.p90 * 100).toFixed(1) })} boxLvl={getMddLvl(mddP50)} textLvl={mddP50 < 20 ? 3 : 4} />
            </>;
        } else {
            const getGoalLvl = val => val >= 85 ? 1 : val >= 70 ? 2 : val >= 50 ? 3 : 4;
            const mddP50 = (sr.mddPercentiles?.p50 * 100) || 0;
            return <>
                <KPIBox label={t('montecarlo.kpi.goalRate')} value={`${sr.goalMetrics?.successRate?.toFixed(1) || 0}%`} sub={t('montecarlo.kpi.goalRateSub', { amount: Number(inputs.targetAmount || 0).toLocaleString(), currency: currencySymbol })} boxLvl={getGoalLvl(sr.goalMetrics?.successRate)} textLvl={getGoalLvl(sr.goalMetrics?.successRate)} />
                <KPIBox label={t('montecarlo.kpi.projectedAssets', { years: inputs.simYears })} value={`${Math.round(sr.medianFinalValue || 0).toLocaleString()}${currencySymbol}`} sub={t('montecarlo.kpi.projectedAssetsSub', { amount: Math.round(sr.finalValuePercentiles?.p10 || 0).toLocaleString(), currency: currencySymbol })} boxLvl={sr.medianFinalValue >= inputs.totalInvestment ? 1 : 4} textLvl={0} />
                <KPIBox label={t('montecarlo.kpi.maxDrawdown')} value={`-${mddP50.toFixed(1)}%`} sub={t('montecarlo.kpi.maxDrawdownSub', { value: (sr.mddPercentiles?.p90 * 100).toFixed(1) })} boxLvl={mddP50 < 20 ? 2 : mddP50 < 35 ? 3 : 4} textLvl={mddP50 < 20 ? 3 : 4} />
            </>;
        }
    };

    return (
        <div className={styles.monteCarloPage}>
            <main className={commonStyles.Container}>
                <div className={commonStyles.introContainer}>
                    <h1 className={commonStyles.introMain}>{t('montecarlo.intro')}</h1>
                    <p className={commonStyles.introSub}>{t('montecarlo.introSub')}</p>
                </div>

                <section className={styles.topSection}>
                    <div className={styles.settingsCard}>
                        <div className={styles.inputSectionTitle}>Simulation Strategy</div>
                        
                        <div className={styles.controlGroup}>
                            <label className={styles.label}>{t('montecarlo.mode')}</label>
                            <div className={styles.toggleContainer}>
                                <button className={`${styles.toggleBtn} ${!inputs.isWithdrawalMode ? styles.active : ''}`} onClick={() => updateInput('isWithdrawalMode', false)}>📈 {t('montecarlo.modeAccumulate')}</button>
                                <button className={`${styles.toggleBtn} ${inputs.isWithdrawalMode ? styles.active : ''}`} onClick={() => updateInput('isWithdrawalMode', true)}>🏖️ {t('montecarlo.modeWithdraw')}</button>
                            </div>
                        </div>

                        <div className={styles.controlGroup}>
                            <label className={styles.label}>{t('montecarlo.duration')} <span className={styles.textPrimary}>{inputs.simYears}{t('montecarlo.yearUnit', 'yr')}</span></label>
                            <div className={styles.sliderContainer}>
                                <input type="range" min="5" max="40" step="1" value={inputs.simYears} onChange={(e) => { const val = Number(e.target.value); updateInput('simYears', val); if(inputs.minSurvivalYears > val) updateInput('minSurvivalYears', val); }} />
                            </div>
                        </div>

                        <div className={styles.controlGroup}>
                            <label className={styles.label}>{t('montecarlo.simCount')}</label>
                            <div className={styles.pillGroup}>
                                {[1000, 5000, 10000].map(c => (
                                    <button key={c} className={`${styles.pillBtn} ${inputs.simCount === c ? styles.active : ''}`} onClick={()=>updateInput('simCount', c)}>
                                        {c === 1000 ? t('montecarlo.simSpeed.fast') : c === 5000 ? t('montecarlo.simSpeed.normal') : t('montecarlo.simSpeed.precise')} ({c.toLocaleString()})
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className={styles.inputSectionTitle}>Financial Details</div>
                        <div className={`${styles.flexRow} ${styles.mb10}`}>
                            <div className={styles.flex1}>
                                <label className={styles.label}>{t('montecarlo.currency')}</label>
                                <select className={styles.inputField} value={inputs.currency} onChange={(e) => updateInput('currency', e.target.value)}>
                                    <option value="KRW">KRW (₩)</option><option value="USD">USD ($)</option><option value="EUR">EUR (€)</option>
                                </select>
                            </div>
                            <div className={`${styles.flex2} ${styles.inputWrapper}`}>
                                <label className={styles.label}>{t('montecarlo.totalInvestment')}</label>
                                <NumericFormat value={inputs.totalInvestment} thousandSeparator onValueChange={(v) => updateInput('totalInvestment', v.value)} className={styles.inputField} placeholder="0" />
                                <span className={styles.currencyBadge}>{currencySymbol}</span>
                            </div>
                        </div>

                        <div className={styles.dynamicInputArea}>
                            {!inputs.isWithdrawalMode ? (
                                <>
                                    <div className={styles.flexRow}>
                                        <div className={`${styles.flex2} ${styles.inputWrapper}`}>
                                            <label className={styles.label}>{t('montecarlo.monthlyCont')}</label>
                                            <NumericFormat value={inputs.monthlyContribution} thousandSeparator onValueChange={(v) => updateInput('monthlyContribution', v.value)} className={styles.inputField} placeholder="0" />
                                            <span className={styles.currencyBadge}>{currencySymbol}</span>
                                        </div>
                                        <div className={styles.flex1}>
                                            <label className={styles.label}>{t('montecarlo.contIncreaseLabel')}</label>
                                            <input type="number" value={inputs.contributionIncrease} onChange={(e) => updateInput('contributionIncrease', e.target.value)} className={`${styles.inputField} ${styles.textCenter}`} />
                                        </div>
                                    </div>
                                    <div className={`${styles.mt10} ${styles.inputWrapper}`}>
                                        <label className={styles.label}>{t('montecarlo.target')}</label>
                                        <NumericFormat value={inputs.targetAmount} thousandSeparator onValueChange={(v) => updateInput('targetAmount', v.value)} className={`${styles.inputField} ${styles.inputValid}`} placeholder={t('montecarlo.targetPlaceholder')} />    
                                        <span className={styles.currencyBadge}>{currencySymbol}</span>
                                    </div>
                                </>
                            ) : (
                                <>
                                    {/* 출금 모드 연동형 UI 적용 */}
                                    <div className={styles.flexRow}>
                                        <div className={styles.flex2}>
                                            <div className={styles.withdrawalHeader}>
                                                <label className={styles.label}>
                                                    {t('montecarlo.withdrawalSetting')}
                                                </label>
                                                <div className={styles.pillGroup}>
                                                    <button 
                                                        className={`${styles.pillBtn} ${inputs.withdrawalInputMode === 'rate' ? styles.active : ''}`}
                                                        onClick={() => updateInput('withdrawalInputMode', 'rate')}
                                                    >
                                                        {t('montecarlo.withdrawalModeRate')}
                                                    </button>
                                                    <button 
                                                        className={`${styles.pillBtn} ${inputs.withdrawalInputMode === 'amount' ? styles.active : ''}`}
                                                        onClick={() => updateInput('withdrawalInputMode', 'amount')}
                                                    >
                                                        {t('montecarlo.withdrawalModeAmount')}
                                                    </button>
                                                </div>
                                            </div>
                                            
                                            {inputs.withdrawalInputMode === 'amount' ? (
                                                <div>
                                                    <div className={styles.inputWrapper}>
                                                        <NumericFormat 
                                                            value={inputs.withdrawalAmount} 
                                                            thousandSeparator 
                                                            onValueChange={(v) => updateInput('withdrawalAmount', v.value)} 
                                                            className={styles.inputField} 
                                                            placeholder={t('montecarlo.withdrawalPlaceholder')} 
                                                        />
                                                        <span className={styles.currencyBadge}>{currencySymbol} / {t('montecarlo.perMonth')}</span>
                                                    </div>
                                                    <div className={styles.smartFeedback}>
                                                        {t('montecarlo.annualWithdrawal', { 
                                                            amount: (inputs.withdrawalAmount * 12).toLocaleString(), 
                                                            currency: currencySymbol 
                                                        })}
                                                        <br />
                                                        {t('montecarlo.annualRate', { rate: inputs.withdrawalRate })}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div>
                                                    <div className={styles.inputWrapper}>
                                                        <input 
                                                            type="number" 
                                                            value={inputs.withdrawalRate} 
                                                            onChange={(e) => updateInput('withdrawalRate', e.target.value)} 
                                                            className={styles.inputField} 
                                                            placeholder="4.0" 
                                                        />
                                                        <span className={styles.currencyBadge}>% ({t('montecarlo.unitYear')})</span>
                                                    </div>
                                                    <div className={`${styles.smartFeedback} ${styles.successTheme}`}>
                                                        {t('montecarlo.monthlyAvailable', { 
                                                            amount: Number(inputs.withdrawalAmount).toLocaleString(), 
                                                            currency: currencySymbol 
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* 우측 인플레이션 입력부 (툴팁 적용됨) */}
                                        <div className={styles.flex1}>
                                           <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '8px' }}>
                                            <label className={styles.label} style={{ marginBottom: 0 }}>
                                                {t('montecarlo.inflationLabel')}
                                            </label>
                                            <div 
                                                ref={inflationTooltipRef} 
                                                className={`${styles.infoIconWrapper} ${isInflationTooltipOpen ? styles.open : ''}`} 
                                                onClick={() => setIsInflationTooltipOpen(!isInflationTooltipOpen)} 
                                                // style={{ cursor: 'pointer', position: 'relative', display: 'inline-flex' }}
                                            >
                                                <span style={{ fontSize: '0.85rem' }}><strong>ℹ️</strong></span>
                                                {/* style 속성을 완전히 지우고 클래스만 적용합니다 */}
                                                <div className={`${styles.tooltipContent} ${styles.inflationTooltip}`}>
                                                    {t('montecarlo.inflationTooltip')}
                                                </div>

                                                
                                                {/* 인라인 스타일을 제거하고 반응형 클래스 추가 */}
                                                {/* <div className={`${styles.tooltipContent} ${styles.inflationTooltip}`}>
                                                    {t('montecarlo.inflationTooltip', '물가 상승률에 맞춰 매년 인출액(생활비)을 증액하여 실제 구매력을 동일하게 유지하도록 계산에 반영합니다.')}
                                                </div> */}
                                            </div>
                                        </div>
                                        <div className={styles.inputWrapper}>
                                            <input 
                                                type="number" value={inputs.inflationRate} 
                                                onChange={(e) => updateInput('inflationRate', e.target.value)} 
                                                className={styles.inputField} placeholder="3.0" />
                                            <span className={styles.currencyBadge}>%</span>
                                        </div>
                                        </div>
                                    </div>
                                    <div className={styles.mt10}>
                                        <label className={styles.label}>{t('montecarlo.minSurvivalLabel')} <span className={styles.textPrimary}>{inputs.minSurvivalYears}{t('montecarlo.durationUnit')}</span></label>
                                        <div className={styles.sliderContainer}>
                                            <input type="range" min="1" max={inputs.simYears} value={inputs.minSurvivalYears} onChange={(e) => updateInput('minSurvivalYears', Math.min(Number(e.target.value), inputs.simYears))} />
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
 
                    <div className={styles.portfolioCard}>
                        <h3 className={styles.cardTitle}>{t('risk.portfolioInput.title', 'Portfolio Composition')}</h3>
                        <div className={styles.tickerList}>
                            {rows.map((row, idx) => (
                                <div key={idx} className={styles.tickerRowItem}>
                                    <div className={styles.tickerRowInput}>
                                        <TickerRow value={row.ticker} onChange={(v) => handleRowChange(idx, 'ticker', v)} disableDefaultStyles={true} placeholder="Ticker (e.g. SPY)" />
                                    </div>
                                    <div className={`${styles.tickerRowWeight} ${styles.inputWrapper}`}>
                                        <NumericFormat value={row.weight} placeholder="0" onValueChange={(v) => handleRowChange(idx, 'weight', v.value)} className={`${styles.inputField} ${styles.textCenter}`} />
                                        <span className={styles.currencyBadge} style={{right:'8px', fontSize:'0.8rem'}}>%</span>
                                    </div>
                                    <button onClick={() => setRows(rows.filter((_, i) => i !== idx))} className={styles.removeBtn}>✕</button>
                                </div>
                            ))}
                        </div>

                        <div className={styles.weightDisplay}>
                            Total Weight: <span className={Math.abs(weightSum - 100) < 0.1 ? styles.textGreen : weightSum > 100 ? styles.textRed : styles.textOrange}>{weightSum.toFixed(1)}%</span>
                        </div>

                        <div className={styles.buttonGroup}>
                            <button className={`${styles.actionBtn} ${styles.btnAdd} ${styles.btnTeal}`} onClick={() => {toast.info(t('loadExampleDisclaimer')); setRows(exampleTickers);}} disabled={isLoading}>{t('montecarlo.loadExample')}</button>
                        </div>
                        <div className={styles.buttonGroup}>
                            <button onClick={() => setRows([...rows, { ticker: "", weight: "" }])} className={`${styles.actionBtn} ${styles.btnAdd}`}>{t('montecarlo.addTicker')}</button>
                            <button className={`${styles.actionBtn} ${styles.btnAdd} ${styles.btnRed}`} onClick={() => setRows([{ ticker: "", weight: "" }])} disabled={isLoading}>{t('montecarlo.reset')}</button>
                        </div>

                        {/* 포트폴리오 불러오기/저장 영역 */}
                        <div className={styles.buttonGroup}>
                            <button onClick={() => actions.fetchUserPortfolios(false)} className={`${styles.actionBtn} ${styles.btnSecondary}`}>{t('montecarlo.loadMyPortfolio')}</button>
                            <button onClick={actions.handleOpenSaveModal} className={`${styles.actionBtn} ${styles.btnSecondary} ${styles.btnBlue}`}>{t('montecarlo.saveCurrentPortfolio')}</button>
                        </div>

                        {actions.isLoadModalOpen && actions.userPortfolios.length > 0 && (
     <div className={styles.portfolioDropdown}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <h3 className={styles.dropdownTitle}>{t('backtest.selectPortfolio') || '포트폴리오를 선택하세요'}</h3>
            <button 
                onClick={() => actions.setIsLoadModalOpen(false)} 
                className={styles.closeDropdownBtn}
                title="Close"
            >
                ✕
            </button>
        </div>
        <ul className={styles.dropdownList}>
            {actions.userPortfolios.map(p => {
                const isAll = p.portfolioType === 'ALL';
                return (
                    <li 
                        key={p.portfolioId} 
                        className={styles.dropdownItem} 
                        style={{
                            fontWeight: isAll ? 'bold' : 'normal', 
                            backgroundColor: isAll ? '#e9f7ff' : '#ffffff', 
                            color: isAll ? '#007bff' : '#334155',
                        }}
                    >
                        {/* 포트폴리오 이름 영역: 클릭 시 포트폴리오 로드 */}
                        <span onClick={() => actions.handleLoadPortfolio(p)} style={{ flexGrow: 1, display: 'block', padding: '4px 0' }}>
                            {p.name}
                        </span>
                        
                        {/* 성과 추적 시작 버튼: PRIVATE 포트폴리오에만 노출 */}
                        {p.portfolioType === 'PRIVATE' && (
                            <button 
                                className={styles.trackingBtn}
                                onClick={(e) => { 
                                    e.stopPropagation(); 
                                    actions.handleStartTrackingPortfolio(p); 
                                }} 
                                disabled={actions.isPortfoliosLoading}
                            >
                                {t('backtest.startTracking') || '성과 추적 시작'}
                            </button>
                        )}
                    </li>
                );
            })}
        </ul>
     </div>
)}

                        {/* {actions.isLoadModalOpen && actions.userPortfolios.length > 0 && (
                             <div className={styles.portfolioDropdown}>
                                <button 
                                    onClick={() => actions.setIsLoadModalOpen(false)} 
                                    className={styles.closeDropdownBtn}
                                    title="Close"
                                >
                                    ✕
                                </button>
                                <ul className={styles.dropdownList}>
                                    {actions.userPortfolios.map(p => <li key={p.portfolioId} className={styles.dropdownItem} onClick={() => actions.handleLoadPortfolio(p)}>{p.name}</li>)}
                                </ul>
                             </div>
                        )} */}

                        <div className={styles.mt10}>
                            <button onClick={handleRunSimulation} disabled={isLoading} className={`${styles.actionBtn} ${styles.btnPrimary} ${styles.fullWidth}`}>
                                {isLoading ? <div className={styles.spinnerSmall}></div> : t('risk.portfolioInput.calculate', 'Run Simulation')}
                            </button>
                        </div>
                    </div>
                </section>
                
                {simulationResults && metaInfo.commonPeriod && (
                    <>
                        <ShareButton onClick={handleShareLink} t={t} />
                        <div className={styles.dataInfoBox}>
                            <div ref={infoTooltipRef} className={`${styles.infoIconWrapper} ${isInfoTooltipOpen ? styles.open : ''}`} onClick={() => setIsInfoTooltipOpen(!isInfoTooltipOpen)} style={{ cursor: 'pointer' }}>
                                <p className={styles.infoTitle}><strong>ℹ️ </strong>{metaInfo.commonPeriod.start} ~ {metaInfo.commonPeriod.end} ({metaInfo.commonPeriod.count} days)</p>
                                <div className={styles.tooltipContent}>{t('montecarlo.dataInfo.description')}</div>
                            </div>
                        </div>
                    </>
                )}

                {simulationResults && (
                    <section className={styles.resultsSection}>
                        <div className={styles.kpiGrid}>{renderKPIs()}</div>

                        <MilestoneTable 
                            simulationResults={simulationResults} milestoneData={milestoneData} 
                            inputs={inputs} calculateMilestoneData={calculateMilestoneData} 
                            t={t} showAllYears={showAllYears} setShowAllYears={setShowAllYears} 
                        />

                        <div className={styles.chartContainer}>
                            <h3 className={styles.chartTitle}>{t('montecarlo.chart.growthTitle')}</h3>
                            <div className={styles.chartHeightLarge}>
                                <GrowthChart results={simulationResults} simYears={inputs.simYears} currency={inputs.currency} targetAmount={inputs.isWithdrawalMode ? 0 : Number(inputs.targetAmount)} t={t} currentYear={growthYear} isPlaying={isGrowthPlaying} />
                            </div>
                            <MiniPlayer year={growthYear} setYear={setGrowthYear} isPlaying={isGrowthPlaying} setIsPlaying={setIsGrowthPlaying} maxYear={inputs.simYears} t={t} />
                        </div>

                        <div className={styles.flexCharts}>
                            <div className={`${styles.chartContainer} ${styles.halfWidth}`}>
                                <h3 className={styles.chartTitle}>{t('montecarlo.chart.finalDistTitle')}</h3>
                                <div className={styles.chartHeightMedium}>
                                    <HistogramChart dataValues={simulationResults.allPathsAtYear?.[distYear] || []} color="#10b981" t={t} currency={inputs.currency} xTitle="Asset Value" fixedMaxX={simulationResults.finalValues ? Math.max(...simulationResults.finalValues) : undefined} />
                                </div>
                                <MiniPlayer year={distYear} setYear={setDistYear} isPlaying={isDistPlaying} setIsPlaying={setIsDistPlaying} maxYear={inputs.simYears} t={t} />
                            </div>

                            <div className={`${styles.chartContainer} ${styles.halfWidth}`}>
                                <h3 className={styles.chartTitle}>{t('montecarlo.chart.mddTitle')}</h3>
                                <div className={styles.chartHeightMedium}>
                                    <HistogramChart dataValues={simulationResults.allMddAtYear?.[mddYear] || []} color="#f43f5e" t={t} xTitle="Drawdown (%)" isLowerBetter={true} fixedMaxX={(() => { if (!simulationResults.allMddAtYear) return undefined; const maxYear = Math.max(...Object.keys(simulationResults.allMddAtYear).map(Number)); return Math.max(...(simulationResults.allMddAtYear[maxYear] || [0])); })()} />
                                </div>
                                <MiniPlayer year={mddYear} setYear={setMddYear} isPlaying={isMddPlaying} setIsPlaying={setIsMddPlaying} maxYear={inputs.simYears} t={t} />
                            </div>
                        </div>
                    </section>
                )}


                {simulationResults && metaInfo.commonPeriod && <ChallengeCta stocks={stocks} optimizedWeights={optimizedWeights} />}
                {simulationResults && metaInfo.commonPeriod && <ShareButton onClick={handleShareLink} t={t} />}

                {actions.showSaveModal && (
                    <NewPortfolioModal onClose={() => actions.setShowSaveModal(false)} onCreated={actions.handlePortfolioSaved} initialHoldings={actions.tempHoldings} initialName={''} isForcedPublic={false} />
                )}
            </main>
            {/* <ToastContainer position="bottom-right" /> */}
            <ToastContainer position="top-center" autoClose={5000} />
        </div>
    );
}

export default MonteCarlo;